/**
 * The canvas message input — the exact quick-chat composer input, decoupled from any
 * positioning/chrome. It wires the shared `MessageInput` to the canvas's selection +
 * file-record text-selection referenced entities and the canvas reasoning-effort store,
 * then hands the merged result to `onSubmit`.
 *
 * Consumers supply their own container (free-floating quick-chat panel, centered landing
 * card, bottom dock). This keeps the input pixel-identical everywhere it appears.
 */

import { MessageInput, type MessageInputRef } from "@/components/MessageInput";
import { ReferencedEntitiesPills } from "@/components/ReferencedEntitiesPills";
import { MessageInputControlSet } from "@/constants/message-input";
import { MessageInputProvider } from "@/context/MessageInputContext";
import { SessionProvider } from "@/context/SessionContext";
import { DropZoneProvider } from "@/dnd/OsFileDropLayer";
import { useCanvasHarnessDraft } from "@/hooks/useHarnessDraft";
import { useCanvasSelectionMirror } from "@/pages/canvas/context/CanvasSelectionMirrorContext";
import { useCanvasReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { useFileRecordTextSelectionReferencedEntity } from "@/panes/file-record-detail/useFileRecordTextSelectionReferencedEntity";
import {
  mergeCanvasReferencedEntities,
  mergeMessageAndCanvasReferencedEntities,
} from "@/utils/mergeReferencedEntities";
import type { PostMessageParams, ReferencedEntity } from "@cohesive/api-client";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type Ref,
} from "react";
import { CANVAS_SESSION_MESSAGE_INPUT_CLASSNAMES } from "../config/canvasSessionMessageInputClassNames";
import { convertNodesToReferencedEntities } from "../utils/convertNodesToReferencedEntities";

/** Imperative handle so containers can prefill/focus the input (e.g. landing suggestion cards). */
export interface CanvasComposerInputHandle {
  /** Replace the editor text and focus it (does not send). */
  setMessage: (text: string) => void;
  /** Move keyboard focus into the editor. */
  focus: () => void;
  /** Remove a referenced entity (canvas selection / text excerpt) from the outgoing message. */
  removeEntity: (entityId: string) => void;
}

/** The `classNames` shape accepted by the shared `MessageInput`. */
type MessageInputClassNames = NonNullable<
  ComponentProps<typeof MessageInput>["classNames"]
>;

export interface CanvasComposerInputResult {
  /** True once the flow message was posted (selections should leave the cache). */
  messageSent: boolean;
}

interface CanvasComposerInputProps {
  canvasId: string;
  /** When set, drafts are scoped to this session instead of the canvas. */
  flowSessionId?: string;
  /**
   * Called with the trimmed text + merged referenced entities + the per-message tail of the
   * send params, forwarded as one object so a new field reaches every seat without another
   * positional. `model` / `permission_mode` are set only where this composer drives a session
   * that already exists and the user switched them — a composer that mints its own session
   * carries those choices on the create (model) or its first message (mode).
   */
  onSubmit: (
    content: string,
    referencedEntities: ReferencedEntity[],
    options: Pick<
      PostMessageParams,
      "reasoning_effort" | "model" | "permission_mode"
    >,
  ) => Promise<CanvasComposerInputResult>;
  isSubmitting?: boolean;
  /** Pre-filled text (e.g. from voice transcription). One-time hydration. */
  initialContent?: string;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  classNames?: MessageInputClassNames;
  /** Fires on every keystroke with the current editor text. */
  onMessageChange?: (message: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Fires when the merged referenced entities (canvas selection + text excerpts) change. */
  onReferencedEntitiesChange?: (entities: ReferencedEntity[]) => void;
  /** Hide the in-input referenced-entity pills (the dock renders its own grouped row instead). */
  suppressInternalPills?: boolean;
  handleRef?: Ref<CanvasComposerInputHandle>;
}

/**
 * Inner input — must run inside the provider stack below. Reads the live canvas
 * node selection (via CanvasSelectionMirrorContext that CanvasPage
 * publishes) and file-record text excerpts, merges them as referenced entities,
 * and renders the shared `MessageInput`.
 */
function CanvasComposerInputInner({
  canvasId,
  flowSessionId,
  onSubmit,
  isSubmitting = false,
  initialContent,
  placeholder = "What would you like me to do?",
  autoFocus,
  className,
  classNames = CANVAS_SESSION_MESSAGE_INPUT_CLASSNAMES,
  onMessageChange,
  onFocus,
  onBlur,
  onReferencedEntitiesChange,
  suppressInternalPills = false,
  handleRef,
}: CanvasComposerInputProps) {
  const inputRef = useRef<MessageInputRef>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const focusEditor = useCallback(() => {
    wrapperRef.current
      ?.querySelector<HTMLElement>('[contenteditable="true"]')
      ?.focus();
  }, []);

  const selectedNodes = useCanvasSelectionMirror();

  const {
    textSelectionEntities,
    removeSelection,
    clearSelectionsFromMessage: clearTextSelectionsFromMessage,
    isTextSelectionEntityId,
  } = useFileRecordTextSelectionReferencedEntity(canvasId);

  const [removedEntityIds, setRemovedEntityIds] = useState<Set<string>>(
    new Set(),
  );

  const selectedNodeIds = useMemo(
    () => selectedNodes.map((n) => n.id).join(","),
    [selectedNodes],
  );

  useEffect(() => {
    setRemovedEntityIds(new Set());
  }, [selectedNodeIds]);

  const allReferencedEntities = useMemo(
    () => convertNodesToReferencedEntities(selectedNodes),
    [selectedNodes],
  );

  const referencedEntities = useMemo(
    () =>
      mergeCanvasReferencedEntities(
        allReferencedEntities,
        textSelectionEntities,
        removedEntityIds,
      ),
    [allReferencedEntities, removedEntityIds, textSelectionEntities],
  );

  // Surface the merged entities so a parent (the dock) can render its own grouped pills.
  useEffect(() => {
    onReferencedEntitiesChange?.(referencedEntities);
  }, [referencedEntities, onReferencedEntitiesChange]);

  const handleRemoveEntity = useCallback(
    (entityId: string) => {
      if (isTextSelectionEntityId(entityId)) {
        removeSelection(entityId);
        return;
      }
      setRemovedEntityIds((prev) => new Set([...prev, entityId]));
    },
    [removeSelection, isTextSelectionEntityId],
  );

  useImperativeHandle(
    handleRef,
    () => ({
      setMessage: (text: string) => {
        inputRef.current?.setValue(text);
        focusEditor();
      },
      focus: focusEditor,
      removeEntity: handleRemoveEntity,
    }),
    [focusEditor, handleRemoveEntity],
  );

  const [reasoningEffort, setReasoningEffort] =
    useCanvasReasoningEffort(canvasId);
  // Persisted per canvas alongside the effort tier, and read by every canvas path that mints a
  // session — so the pill states the same choice the create will stamp.
  const [harnessDraft, setHarnessDraft] = useCanvasHarnessDraft(canvasId);

  const handleSend = useCallback(
    async (params: PostMessageParams) => {
      const text = params.content?.trim() ?? "";
      if (!text) return;
      const merged = mergeMessageAndCanvasReferencedEntities(
        params.referenced_entities,
        referencedEntities,
      );
      const { messageSent } = await onSubmit(text, merged, {
        reasoning_effort: params.reasoning_effort,
        model: params.model,
        permission_mode: params.permission_mode,
      });
      if (messageSent) {
        clearTextSelectionsFromMessage(merged);
        inputRef.current?.setValue("");
        onMessageChange?.("");
      }
    },
    [
      onSubmit,
      referencedEntities,
      clearTextSelectionsFromMessage,
      onMessageChange,
    ],
  );

  return (
    <div ref={wrapperRef} className="contents">
      <MessageInput
        ref={inputRef}
        flowSessionId={flowSessionId}
        canvasId={canvasId}
        onSend={handleSend}
        attachedFiles={[]}
        sendButtonDisabled={isSubmitting}
        controlSet={MessageInputControlSet.CANVAS_CHAT}
        location="sidebar"
        placeholder={placeholder}
        autoFocus={autoFocus}
        defaultValue={initialContent}
        onMessageChange={onMessageChange}
        onFocus={onFocus ? () => onFocus() : undefined}
        onBlur={onBlur ? () => onBlur() : undefined}
        className={className}
        classNames={classNames}
        agentReasoningEffort={reasoningEffort}
        onAgentReasoningEffortChange={setReasoningEffort}
        harnessDraft={harnessDraft}
        onHarnessDraftChange={setHarnessDraft}
        middleToolbarSlot={
          !suppressInternalPills && referencedEntities.length > 0 ? (
            <ReferencedEntitiesPills
              entities={referencedEntities}
              onRemove={handleRemoveEntity}
              className="px-0 py-0"
            />
          ) : null
        }
      />
    </div>
  );
}

/**
 * Reusable canvas message input including the provider stack
 * (`DropZoneProvider` → `MessageInputProvider` → `SessionProvider`) the editor
 * needs. Drop it into any container.
 */
export function CanvasComposerInput(props: CanvasComposerInputProps) {
  return (
    <DropZoneProvider
      key={`canvas-composer-${props.canvasId}`}
      className="flex flex-col min-h-0"
    >
      <MessageInputProvider>
        <SessionProvider>
          <CanvasComposerInputInner {...props} />
        </SessionProvider>
      </MessageInputProvider>
    </DropZoneProvider>
  );
}
